import { FormEvent, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  Calendar,
  RefreshCw,
  Trophy,
  ArrowRight,
  ShieldCheck,
  X,
  History,
  AlertCircle,
  Search,
  Filter,
} from "lucide-react";

import PageGate from "../../../components/PageGate";
import AccessGate from "../../../components/AccessGate";
import { PERMS } from "../../../utils/perms";

import {
  useCreateChapterDietMutation,
  useGetChapterDietsQuery,
} from "../api/chapterApi";

function isActive(d: any) {
  return d?.is_active === true || d?.is_active === 1;
}

export default function ChapterDietsPage() {
  const { data = [], isLoading, error, refetch } = useGetChapterDietsQuery();
  const [createDiet, { isLoading: creating }] = useCreateChapterDietMutation();

  // States for Search and Filtering
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "closed">(
    "all"
  );

  const activeExists = useMemo(() => data.some(isActive), [data]);

  // Optimized Filter Logic
  const filteredDiets = useMemo(() => {
    return data.filter((d: any) => {
      const matchesSearch = d.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const isCurrentlyActive = isActive(d);
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && isCurrentlyActive) ||
        (statusFilter === "closed" && !isCurrentlyActive);

      return matchesSearch && matchesStatus;
    });
  }, [data, searchTerm, statusFilter]);

  const [form, setForm] = useState({ name: "", start_date: "", end_date: "" });
  const [msg, setMsg] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    setMsg(null);
    try {
      await createDiet({
        name: form.name,
        start_date: form.start_date || undefined,
        end_date: form.end_date || undefined,
      }).unwrap();
      setMsg({ type: "success", text: "Diet created successfully" });
      setForm({ name: "", start_date: "", end_date: "" });
      refetch();
    } catch (err: any) {
      setMsg({ type: "error", text: err?.data?.message || "Create failed" });
    }
  };

  return (
    <PageGate perm={PERMS.chapter.dietView}>
      <div className="space-y-6">
        {/* Header Section */}
        <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                <Trophy className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl font-black text-gray-900 tracking-tight">
                  Chapter Diets
                </h1>
                <p className="text-gray-500 text-sm mt-1 font-medium italic">
                  Manage leadership terms and active operational periods.
                </p>
              </div>
            </div>

            <button
              onClick={() => refetch()}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl border bg-white hover:bg-gray-50 text-sm font-semibold transition-all active:scale-95"
            >
              <RefreshCw
                className={`w-4 h-4 text-indigo-500 ${
                  isLoading && "animate-spin"
                }`}
              />{" "}
              Refresh Data
            </button>
          </div>

          {msg && (
            <div
              className={`mt-6 p-4 rounded-xl border flex justify-between items-center animate-in fade-in slide-in-from-top-2 ${
                msg.type === "success"
                  ? "bg-green-50 border-green-100 text-green-800"
                  : "bg-red-50 border-red-100 text-red-800"
              }`}
            >
              <span className="text-sm font-bold tracking-tight">
                {msg.text}
              </span>
              <button onClick={() => setMsg(null)}>
                <X className="w-4 h-4 opacity-50 hover:opacity-100" />
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Create Form */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm sticky top-6">
              <div className="flex items-center gap-2 mb-6 border-b border-gray-50 pb-4">
                <Plus className="w-4 h-4 text-indigo-400" />
                <div className="font-black text-gray-900 text-[10px] uppercase tracking-widest">
                  Initiate New Diet
                </div>
              </div>

              <AccessGate perm={PERMS.chapter.dietCreate}>
                <form onSubmit={onCreate} className="space-y-5">
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-tighter">
                      Diet Name
                    </label>
                    <input
                      placeholder="e.g., 2026/2027 Session"
                      value={form.name}
                      onChange={(e) =>
                        setForm((s) => ({ ...s, name: e.target.value }))
                      }
                      className="w-full border-b-2 border-gray-100 py-2 font-bold text-gray-800 outline-none focus:border-indigo-500 transition-all placeholder:text-gray-300 disabled:opacity-30"
                      disabled={creating || activeExists}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-tighter">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={form.start_date}
                        onChange={(e) =>
                          setForm((s) => ({ ...s, start_date: e.target.value }))
                        }
                        className="w-full border-b-2 border-gray-100 py-2 text-sm font-bold text-gray-800 outline-none focus:border-indigo-500 transition-all disabled:opacity-30"
                        disabled={creating || activeExists}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-tighter">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={form.end_date}
                        onChange={(e) =>
                          setForm((s) => ({ ...s, end_date: e.target.value }))
                        }
                        className="w-full border-b-2 border-gray-100 py-2 text-sm font-bold text-gray-800 outline-none focus:border-indigo-500 transition-all disabled:opacity-30"
                        disabled={creating || activeExists}
                      />
                    </div>
                  </div>

                  {activeExists ? (
                    <div className="p-4 bg-amber-50 rounded-2xl border border-amber-100 flex gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
                      <p className="text-[11px] text-amber-800 font-medium leading-tight">
                        The organization currently has an{" "}
                        <span className="font-black">Active Diet</span>. Please
                        close it before starting a new term.
                      </p>
                    </div>
                  ) : (
                    <button
                      type="submit"
                      disabled={creating}
                      className="w-full bg-indigo-600 text-white rounded-xl py-4 text-[10px] font-black uppercase tracking-[0.2em] hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 disabled:opacity-50 active:scale-95"
                    >
                      {creating ? "Processing..." : "Open New Diet Session"}
                    </button>
                  )}
                </form>
              </AccessGate>
            </div>
          </div>

          {/* Right Column: List with Search/Filters */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white border border-gray-100 rounded-3xl shadow-sm overflow-hidden">
              {/* Filter Bar */}
              <div className="p-4 bg-gray-50/50 border-b border-gray-50 flex flex-col md:flex-row gap-4 items-center">
                <div className="relative flex-1 w-full">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by diet name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 ring-indigo-50 transition-all outline-none"
                  />
                </div>
                <div className="flex items-center gap-2 w-full md:w-auto">
                  <Filter className="w-4 h-4 text-gray-400 hidden md:block" />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as any)}
                    className="flex-1 md:w-32 py-2 px-3 bg-white border border-gray-200 rounded-xl text-xs font-bold uppercase tracking-wider outline-none focus:ring-2 ring-indigo-50"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active Only</option>
                    <option value="closed">Closed Only</option>
                  </select>
                </div>
              </div>

              {/* Table Body */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left bg-white">
                      <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                        Diet Schedule
                      </th>
                      <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">
                        Current Status
                      </th>
                      <th className="px-6 py-4 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">
                        Manage
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filteredDiets.map((d: any) => (
                      <tr
                        key={d.id}
                        className="group hover:bg-gray-50/80 transition-all"
                      >
                        <td className="px-6 py-5">
                          <div className="font-black text-gray-900 group-hover:text-indigo-600 transition-colors">
                            {d.name}
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-[11px] font-bold text-gray-400">
                            <Calendar className="w-3 h-3 text-indigo-300" />
                            <span>{d.start_date || "N/A"}</span>
                            <span className="text-gray-200">|</span>
                            <span>{d.end_date || "N/A"}</span>
                          </div>
                        </td>
                        <td className="px-6 py-5 text-center">
                          <span
                            className={`px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest border-2 shadow-sm ${
                              isActive(d)
                                ? "bg-green-50 text-green-700 border-green-100"
                                : "bg-white text-gray-400 border-gray-100"
                            }`}
                          >
                            {isActive(d) ? "● Active Session" : "Archived"}
                          </span>
                        </td>
                        <td className="px-6 py-5 text-right">
                          <Link
                            to={`/chapter/diets/${d.id}`}
                            className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-gray-900 text-white text-[10px] font-black uppercase tracking-widest hover:bg-indigo-600 hover:shadow-lg hover:shadow-indigo-100 transition-all active:scale-95"
                          >
                            View Details
                          </Link>
                        </td>
                      </tr>
                    ))}

                    {filteredDiets.length === 0 && !isLoading && (
                      <tr>
                        <td colSpan={3} className="px-6 py-24 text-center">
                          <div className="flex flex-col items-center justify-center space-y-3 opacity-20">
                            <Search className="w-12 h-12" />
                            <p className="text-sm font-black uppercase tracking-widest">
                              No matching diets found
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Insight Card */}
            <div className="bg-gradient-to-br from-indigo-600 to-indigo-800 rounded-3xl p-8 text-white shadow-xl shadow-indigo-100 relative overflow-hidden">
              {/* Decorative Background Element */}
              <Trophy className="absolute -right-10 -bottom-10 w-48 h-48 opacity-10 rotate-12" />

              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4 text-indigo-200">
                  <ShieldCheck className="w-5 h-5" />
                  <span className="text-[10px] font-black uppercase tracking-[0.3em]">
                    Governance Protocol
                  </span>
                </div>
                <h3 className="text-xl font-black mb-3 leading-tight">
                  Managing Institutional Diets
                </h3>
                <p className="text-sm text-indigo-100 leading-relaxed max-w-xl opacity-80">
                  A diet represents a formal leadership tenure. While multiple
                  diets can exist in the history, the system architecture
                  strictly permits only{" "}
                  <span className="underline decoration-indigo-400 underline-offset-4">
                    one active term
                  </span>{" "}
                  at a time to maintain data integrity across chapters.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageGate>
  );
}
